import React from 'react';

import { client } from '@oysterjs/ais/api/client';
import { EmailBulkDraftCardDocument } from '@oysterjs/ais/api/graphql';

/**
 * The emails drafted for the given bulk drafts, which the chat shows on each bulk draft's card
 * rather than as cards of their own. It reads only what those cards have read, as they poll.
 */
export const useBulkEmailArtifactIds = (bulkSendIds: string[]): ReadonlySet<string> => {
  const [artifactIdsByBulkSendId, setArtifactIdsByBulkSendId] = React.useState<
    Record<string, string[]>
  >({});
  // Keyed by value, so a new array with the same bulk drafts doesn't watch them again.
  const bulkSendIdsKey = bulkSendIds.join(',');

  React.useEffect(() => {
    const watchedBulkSendIds = bulkSendIdsKey ? bulkSendIdsKey.split(',') : [];
    const subscriptions = watchedBulkSendIds.map((bulkSendId) =>
      client
        // Cache only: it follows the cards' reads, and a failed read can't end the watch.
        .watchQuery({
          query: EmailBulkDraftCardDocument,
          variables: { id: bulkSendId },
          fetchPolicy: 'cache-only'
        })
        .subscribe({
          next: ({ data }) => {
            const artifactIds =
              data?.emailBulkSend?.recipients.flatMap((recipient) => recipient.artifactId ?? []) ??
              [];
            // Each poll answers again, mostly with the same emails, which needn't redraw the chat.
            setArtifactIdsByBulkSendId((last) =>
              last[bulkSendId]?.join(',') === artifactIds.join(',')
                ? last
                : { ...last, [bulkSendId]: artifactIds }
            );
          }
        })
    );
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, [bulkSendIdsKey]);

  return React.useMemo(
    () =>
      new Set(
        bulkSendIdsKey.split(',').flatMap((bulkSendId) => artifactIdsByBulkSendId[bulkSendId] ?? [])
      ),
    [artifactIdsByBulkSendId, bulkSendIdsKey]
  );
};
